// pages/FavoriteBooksPage.tsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFavorites } from '../context/FavoritesContext';
import { fetchFavoriteBooks } from '../utils/favoritesAndHistoryApi';
import { generateCoverUrlSmart } from '../utils/generateCoverUrl';
import { Container, Grid, Card, CardMedia, Typography, Box, CircularProgress } from '@mui/material';
import { useAppTokens } from '../context/ThemeModeContext';

interface FavoriteBookItem {
  rl_book_id: number;
  favorited_at: number;
  title?: string;
  author?: string;
  publisher?: string;
  pub_year?: number;
  isbn13?: string;
  cover_url?: string;
}

const FavoriteBooksPage: React.FC = () => {
  const [favoriteBooks, setFavoriteBooks] = useState<FavoriteBookItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { idToken } = useAuth();
  const { state } = useFavorites();
  const navigate = useNavigate();

  // ===== Style tokens (GraphPage準拠) =====
  const TOKENS = useAppTokens();

  const glassPanelSx = {
    background: TOKENS.glass,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 2,
    boxShadow: '0 0 0 rgba(0,0,0,0)',
    backdropFilter: 'blur(6px)',
  } as const;

  useEffect(() => {
    const loadFavoriteBooks = async () => {
      if (idToken) {
        try {
          setLoading(true);
          const res = await fetchFavoriteBooks(idToken);
          const items: FavoriteBookItem[] = res.data?.items ?? [];
          setFavoriteBooks(items);
        } catch (error) {
          console.error('Error fetching favorite books:', error);
          setError('お気に入りの本の取得に失敗しました。');
        } finally {
          setLoading(false);
        }
      }
    };

    loadFavoriteBooks();
  }, [idToken, state.lastUpdated]);

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getCoverImage = (book: FavoriteBookItem): string => {
    return generateCoverUrlSmart(book.rl_book_id, book.title, book.isbn13);
  };

  // Loading
  if (loading) {
    return (
      <Box
        sx={{
          width: '100vw',
          minHeight: '100dvh',
          background: `radial-gradient(1200px 600px at 20% 10%, ${TOKENS.radialGlow}, transparent 60%), ${TOKENS.bg}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: TOKENS.text,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error
  if (error) {
    return (
      <Box
        sx={{
          width: '100vw',
          minHeight: '100dvh',
          background: `radial-gradient(1200px 600px at 20% 10%, ${TOKENS.radialGlow}, transparent 60%), ${TOKENS.bg}`,
          color: TOKENS.text,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Box sx={{ ...glassPanelSx, p: 2 }}>
          <Typography sx={{ color: TOKENS.error, fontSize: 13 }}>{error}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 7 }}>
      <Box
        sx={{
          width: '100%',
          minHeight: 'calc(100dvh - 56px)',
          background: `radial-gradient(1200px 600px at 20% 10%, ${TOKENS.radialGlow}, transparent 60%), ${TOKENS.bg}`,
          color: TOKENS.text,
          position: 'relative',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* page title */}
        <Box
          sx={{
            position: 'absolute',
            top: 14,
            left: 14,
            zIndex: 5,
            display: 'flex',
            gap: 1.5,
            alignItems: 'baseline',
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: TOKENS.text }}>お気に入り</Typography>
          <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>{favoriteBooks.length}冊</Typography>
        </Box>

        <Container
          maxWidth={false}
          sx={{
            pt: 8,
            pb: 4,
            px: { xs: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              ...glassPanelSx,
              height: { xs: 'auto', md: 'calc(100dvh - 96px)' },
              display: 'flex',
              flexDirection: 'column',
              overflow: { xs: 'visible', md: 'hidden' },
            }}
          >
            {/* header */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${TOKENS.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>お気に入りの本</Typography>
              <Typography sx={{ fontSize: 12, color: TOKENS.textSub }}>最新追加順</Typography>
            </Box>

            {/* body */}
            <Box
              sx={{
                px: 2,
                py: 2,
                overflowY: { xs: 'visible', md: 'auto' },
                WebkitOverflowScrolling: 'touch',
                flex: { xs: 'unset', md: 1 },
                minHeight: 0,
              }}
            >
              {favoriteBooks.length === 0 ? (
                <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
                  お気に入りの本がありません。
                </Typography>
              ) : (
                <Grid container spacing={1.5}>
                  {favoriteBooks.map((book) => (
                    <Grid item xs={6} sm={4} md={3} key={book.rl_book_id}>
                      <Box>
                        <Card
                          onClick={() => {
                            navigate(`/book/${book.rl_book_id}`);
                            window.scrollTo(0, 0);
                          }}
                          sx={{
                            transition: '0.25s',
                            background: TOKENS.cardBg,
                            border: `1px solid ${TOKENS.border}`,
                            borderRadius: 2,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            boxShadow: 'none',
                            '&:hover': {
                              border: `1px solid ${TOKENS.accent}`,
                              background: TOKENS.cardBgHover,
                            },
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={getCoverImage(book)}
                            alt={book.title ?? ''}
                            sx={{
                              objectFit: 'contain',
                              height: { xs: 170, sm: 200 },
                              width: '100%',
                              padding: '10px',
                              background: TOKENS.cardBg,
                            }}
                          />
                        </Card>

                        <Box sx={{ mt: 1, px: 0.25 }}>
                          <Typography
                            sx={{
                              fontSize: 12.5,
                              color: TOKENS.text,
                              lineHeight: 1.25,
                              display: '-webkit-box',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 2,
                              overflow: 'hidden',
                            }}
                          >
                            {book.title}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 12,
                              color: TOKENS.textSub,
                              mt: 0.25,
                              display: '-webkit-box',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 1,
                              overflow: 'hidden',
                            }}
                          >
                            {book.author}
                          </Typography>

                          <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mt: 0.25 }}>
                            {book.pub_year}
                          </Typography>

                          <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mt: 0.5 }}>
                            追加: {formatDate(book.favorited_at)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default FavoriteBooksPage;
